import z from 'zod/v4';
import { CaseFilterSchema } from '../../insights/index.js';
import { queryJSONSchema } from '../../util/validation/index.js';
import { TribunalCaseType } from '../cases/types.js';
import { DKTribunalJobType, tribunalDKJobReferenceCaseMap, } from './countries/denmark.js';
import { GLTribunalJobType, tribunalGLJobReferenceCaseMap, } from './countries/greenland.js';
export var InternationalTribunalJobType;
(function (InternationalTribunalJobType) {
    InternationalTribunalJobType["Distraint"] = "DISTRAINT";
    InternationalTribunalJobType["CourtMeeting"] = "COURT_MEETING";
    InternationalTribunalJobType["Note"] = "NOTE";
    InternationalTribunalJobType["CloseCase"] = "CLOSE_CASE";
})(InternationalTribunalJobType || (InternationalTribunalJobType = {}));
export const TribunalJobType = {
    ...InternationalTribunalJobType,
    // Denmark
    ...DKTribunalJobType,
    // Greenland
    ...GLTribunalJobType,
};
export var CourtMeetingState;
(function (CourtMeetingState) {
    CourtMeetingState["ReadyForMeeting"] = "READY_FOR_MEETING";
    CourtMeetingState["NeedsApproval"] = "NEEDS_APPROVAL";
    CourtMeetingState["Accepted"] = "ACCEPTED";
    CourtMeetingState["Rejected"] = "REJECTED";
    CourtMeetingState["NotesSent"] = "NOTES_SENT";
    CourtMeetingState["NeedsRevision"] = "NEEDS_REVISION";
    CourtMeetingState["Concluded"] = "CONCLUDED";
    CourtMeetingState["Canceled"] = "CANCELED";
})(CourtMeetingState || (CourtMeetingState = {}));
// Named ids for the meeting types referenced directly in code, so consumers
// link to the source of truth below instead of repeating raw UUIDs.
export const MeetingTypeId = {
    AlmindeligtTelefoniskFogedmoede: 'f7cdc8f8-b15e-4394-9048-9350bc5d5107',
    AlmindeligtFogedmoedeUdenFremmoede: 'b3e9d847-7a2c-4f15-9d8e-2c5b1a3f6e4d',
    AlmindeligtFysiskFogedmoede: 'f49dbe03-6d1d-4241-9d65-a33d66ba5ed6',
    JuridiskFogedretsmoede: '1d7bc75a-ab3d-46e5-b8e6-fa0d7fce21cf',
    UdkoerendeFogedforretning: 'ef4787bb-b520-489f-a6a7-c9bf39f68ceb',
    Gassag: '12859ad2-8653-40be-a8a6-a5ccca856afe',
    Moedesag: '88133220-b080-431a-a16e-0248f00f4cb1',
    Moedesag2: '5d72d0e0-32ef-4087-b2d6-3f3c50757541',
    Pantebrevsudlaeg: '0eb70647-127b-4fd6-bd16-bcbbe2112407',
    TvangssalgAfAndelsbolig: '76e8b578-cf77-4147-b070-146580806c87',
    TvangssalgAfBilLoesoere: '1c814d89-977a-4a02-8091-66631206ca8c',
    GenfremsendelseKreditorunddragelse: 'ae0fcfac-6e44-48eb-8d76-5ef1c12ef001',
    Politifremstilling: 'f22eea11-8a78-45ee-8462-01d83b217271',
    TvangssalgAfFastEjendom: 'ba95c96d-7a92-4d16-b745-9f2976d88103',
};
export const meetingTypes = [
    {
        id: MeetingTypeId.AlmindeligtTelefoniskFogedmoede,
        label: 'Almindeligt telefonisk fogedmøde',
        tenantId: null,
        meetingType: 'phone',
        relevantCaseTypes: [
            TribunalCaseType.DKBetalingspaakrav,
            TribunalCaseType.DKFogedrekvisition,
            TribunalCaseType.DKAndreFogedsager,
        ],
    },
    {
        id: MeetingTypeId.AlmindeligtFogedmoedeUdenFremmoede,
        label: 'Almindeligt fogedmøde uden fremmøde',
        tenantId: null,
        meetingType: 'no_attendance',
        relevantCaseTypes: [
            TribunalCaseType.DKBetalingspaakrav,
            TribunalCaseType.DKFogedrekvisition,
            TribunalCaseType.DKAndreFogedsager,
        ],
    },
    {
        id: MeetingTypeId.AlmindeligtFysiskFogedmoede,
        label: 'Almindeligt fysisk fogedmøde',
        tenantId: null,
        meetingType: 'physical',
        relevantCaseTypes: [
            TribunalCaseType.DKBetalingspaakrav,
            TribunalCaseType.DKFogedrekvisition,
            TribunalCaseType.DKAndreFogedsager,
        ],
    },
    {
        id: MeetingTypeId.JuridiskFogedretsmoede,
        label: 'Juridisk fogedretsmøde',
        meetingType: 'physical',
        tenantId: null,
        relevantCaseTypes: [
            TribunalCaseType.DKBetalingspaakrav,
            TribunalCaseType.DKFogedrekvisition,
            TribunalCaseType.DKAndreFogedsager,
        ],
    },
    {
        id: MeetingTypeId.UdkoerendeFogedforretning,
        label: 'Udkørende fogedforretning',
        tenantId: null,
        meetingType: 'physical',
        relevantCaseTypes: [
            TribunalCaseType.DKBetalingspaakrav,
            TribunalCaseType.DKFogedrekvisition,
            TribunalCaseType.DKAndreFogedsager,
        ],
    },
    {
        id: MeetingTypeId.Pantebrevsudlaeg,
        label: 'Pantebrevsudlæg',
        tenantId: null,
        meetingType: 'physical',
        relevantCaseTypes: [
            TribunalCaseType.DKBetalingspaakrav,
            TribunalCaseType.DKFogedrekvisition,
            TribunalCaseType.DKAndreFogedsager,
        ],
    },
    {
        id: MeetingTypeId.TvangssalgAfAndelsbolig,
        label: 'Tvangssalg af andelsbolig',
        meetingType: 'physical',
        tenantId: null,
        relevantCaseTypes: [
            TribunalCaseType.DKBetalingspaakrav,
            TribunalCaseType.DKFogedrekvisition,
            TribunalCaseType.DKAndreFogedsager,
        ],
    },
    {
        id: MeetingTypeId.Politifremstilling,
        label: 'Politifremstilling',
        meetingType: 'physical',
        tenantId: null,
        relevantCaseTypes: [
            TribunalCaseType.DKBetalingspaakrav,
            TribunalCaseType.DKFogedrekvisition,
            TribunalCaseType.DKAndreFogedsager,
        ],
    },
    {
        id: MeetingTypeId.TvangssalgAfFastEjendom,
        label: 'Tvangssalg af fast ejendom',
        meetingType: 'physical',
        tenantId: null,
        relevantCaseTypes: [TribunalCaseType.DKFogedrekvisition],
    },
    {
        id: MeetingTypeId.TvangssalgAfBilLoesoere,
        label: 'Tvangssalg af bil/løsøre',
        meetingType: 'physical',
        tenantId: null,
        relevantCaseTypes: [
            TribunalCaseType.DKBetalingspaakrav,
            TribunalCaseType.DKFogedrekvisition,
            TribunalCaseType.DKAndreFogedsager,
        ],
    },
    {
        id: MeetingTypeId.GenfremsendelseKreditorunddragelse,
        label: 'Genfremsendelse/Kreditorunddragelse',
        meetingType: 'physical',
        tenantId: '50fadc10-ec37-4634-81d8-c1d90ba52cfa',
        relevantCaseTypes: [
            TribunalCaseType.DKBetalingspaakrav,
            TribunalCaseType.DKFogedrekvisition,
            TribunalCaseType.DKAndreFogedsager,
        ],
    },
    {
        id: MeetingTypeId.Moedesag,
        label: 'Mødesag',
        tenantId: '50fadc10-ec37-4634-81d8-c1d90ba52cfa',
        meetingType: 'physical',
        relevantCaseTypes: [
            TribunalCaseType.DKBetalingspaakrav,
            TribunalCaseType.DKFogedrekvisition,
            TribunalCaseType.DKAndreFogedsager,
        ],
    },
    {
        id: MeetingTypeId.Moedesag2,
        label: 'Mødesag',
        tenantId: '50fadc10-ec37-4634-81d8-c1d90ba52cfa',
        meetingType: 'physical',
        relevantCaseTypes: [
            TribunalCaseType.DKBetalingspaakrav,
            TribunalCaseType.DKFogedrekvisition,
            TribunalCaseType.DKAndreFogedsager,
        ],
    },
    {
        id: MeetingTypeId.Gassag,
        label: 'Gassag',
        tenantId: '50fadc10-ec37-4634-81d8-c1d90ba52cfa',
        meetingType: 'physical',
        relevantCaseTypes: [
            TribunalCaseType.DKBetalingspaakrav,
            TribunalCaseType.DKFogedrekvisition,
            TribunalCaseType.DKAndreFogedsager,
        ],
    },
];
export var DistraintType;
(function (DistraintType) {
    DistraintType["Property"] = "PROPERTY";
    DistraintType["Vehicle"] = "VEHICLE";
    DistraintType["RentalDeposit"] = "RENTDEPOSITUM";
    DistraintType["MortgageDeed"] = "MORGAGE_DEED";
    DistraintType["CooperativeHousing"] = "COOPORATIVE_HOUSING";
    DistraintType["ExcessTax"] = "WITHHELD_TAX";
    DistraintType["Other"] = "DIVERSE";
})(DistraintType || (DistraintType = {}));
export const TribunalJobsFilterSchema = z.object({
    types: queryJSONSchema(z.array(z.enum(TribunalJobType)), `type need to be a valid list of types (${Object.values(TribunalJobType)})`).optional(),
    sortKey: z.enum(['meetingDate']).optional(),
    sortDirection: z.enum(['ASC', 'DESC']).optional(),
    jobId: z.string().optional(),
    tribunalCaseId: z.string().optional(),
    debtorUserId: z.uuid().optional(),
    assignee: z.uuid().optional(),
    fromDate: z.iso.date().optional(),
    toDate: z.iso.date().optional(),
    courtIds: queryJSONSchema(z.array(z.uuid()), 'Type needs to be a list of court uuids').optional(),
    states: queryJSONSchema(z.array(z.enum(CourtMeetingState)), `type need to be a valid list of types (${Object.values(CourtMeetingState)})`).optional(),
    meetingTypeIds: queryJSONSchema(z.array(z.uuid()), `type need to be a valid list of uuids`).optional(),
    distraintTypes: queryJSONSchema(z.array(z.enum(DistraintType)), `type need to be a valid list of types (${Object.values(DistraintType)})`).optional(),
    caseFilter: queryJSONSchema(CaseFilterSchema, 'type needs to be a valid case filter').optional(),
});
export const tribunalJobReferenceCaseMap = {
    // Denmark
    ...tribunalDKJobReferenceCaseMap,
    // Greenland
    ...tribunalGLJobReferenceCaseMap,
};
//# sourceMappingURL=types.js.map